import { useEffect, useRef, useState } from 'react';

import { SPEED_PRICE_WS_BASE } from '@/lib/api/speed';
import { useWebSocket } from '@/lib/ws';

import { STALE_THRESHOLD_MS, type PriceFeedState } from '../types';
import { parseMessage } from '../utils/parseMessage';

const WS_BASE_URL = SPEED_PRICE_WS_BASE;

/**
 * Hook to connect to the real-time price feed via WebSocket.
 *
 * Handles:
 * - Connection to `wss://api.wolfswap.app/speed/ws/:networkId/:pair`
 * - Server acknowledgment tracking (`0|pair` message)
 * - Price extraction from `1|price` messages
 * - Ping echoing (`3|timestamp`) back to server
 * - RTT storage from `4|rtt` messages
 * - Staleness detection (no valid price-stream frame for 15s)
 * - Server error handling (`2|error`)
 *
 * @param networkId - Network ID (e.g., 25 for Cronos)
 * @param pair - Trading pair (e.g., 'cro-usdc')
 * @param enabled - Whether to connect (default: true)
 * @returns Price feed state including price, connection state, latency, etc.
 */
export function usePriceFeed(
    networkId: number,
    pair: string,
    enabled: boolean = true,
): PriceFeedState {
    // State
    const [price, setPrice] = useState<number | null>(null);
    const [isServerReady, setIsServerReady] = useState(false);
    const [latency, setLatency] = useState<number | null>(null);
    const [serverError, setServerError] = useState<string | null>(null);
    const [isStale, setIsStale] = useState(false);

    // Refs for liveness tracking. Hyperliquid mids may not change every few
    // seconds, so pongs and connect frames count as fresh data too.
    const lastActivityRef = useRef<number>(Date.now());
    const staleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Build URL (null disables connection)
    const url = enabled ? `${WS_BASE_URL}/${networkId}/${pair}` : null;

    const { state, send, reconnect } = useWebSocket(url, {
        onMessage: (_, event) => {
            const message = parseMessage(event.data);
            const markFresh = () => {
                setIsStale(false);
                lastActivityRef.current = Date.now();
            };

            switch (message.type) {
            case '0': // Connected confirmation
                markFresh();
                setIsServerReady(true);
                setServerError(null);
                break;

            case '1': // Price update
                markFresh();
                setPrice(message.price);
                break;

            case '2': // Error
                markFresh();
                setServerError(message.error);
                break;

            case '4': // RTT reply to our client-initiated ping
                markFresh();
                setLatency(message.rtt);
                break;
            }
        },
        onStateChange: (newState) => {
            if (newState === 'connected') {
                lastActivityRef.current = Date.now();
                setIsStale(false);
            }
            // Reset server ready state on disconnect
            if (newState === 'disconnected' || newState === 'reconnecting') {
                setIsServerReady(false);
            }
        },
    });

    // Staleness detection
    useEffect(() => {
        if (state !== 'connected') {
            setIsStale(false);
            return;
        }

        staleTimerRef.current = setInterval(() => {
            const elapsed = Date.now() - lastActivityRef.current;
            setIsStale(elapsed > STALE_THRESHOLD_MS);
        }, 1000);

        return () => {
            if (staleTimerRef.current) {
                clearInterval(staleTimerRef.current);
            }
        };
    }, [state]);

    // Client-initiated RTT ping: the price stream replies `4|rtt` to a `3|<sentAtMs>`
    // frame, so we drive the latency measurement from the client side.
    useEffect(() => {
        if (state !== 'connected') {
            return;
        }

        const sendPing = () => {
            try {
                send(`3|${Date.now()}`);
            } catch {
                // ignore send errors during reconnection
            }
        };

        sendPing();
        pingTimerRef.current = setInterval(sendPing, 5000);

        return () => {
            if (pingTimerRef.current) {
                clearInterval(pingTimerRef.current);
                pingTimerRef.current = null;
            }
        };
    }, [state, send]);

    // Reset state when pair changes
    useEffect(() => {
        lastActivityRef.current = Date.now();
        setPrice(null);
        setIsServerReady(false);
        setLatency(null);
        setServerError(null);
        setIsStale(false);
    }, [networkId, pair]);

    return {
        price,
        connectionState: state,
        isServerReady,
        latency,
        error: serverError,
        isStale,
        reconnect,
    };
}
